import { createElement, useCallback, useMemo, useRef, useState } from 'react';

import { ContentEditorContext } from '@sayyesschool/frontend/components/content-editor';
import { Flex, IconButton, Text } from '@sayyesschool/frontend/ui-components';
import classnames from '@sayyesschool/frontend/utils/classnames';
import { capitalize } from '@sayyesschool/frontend/utils/string';

import AudioItem from './AudioItem';
import BooleanItem from './BooleanItem';
import ChoiceItem from './ChoiceItem';
import DividerItem from './DividerItem';
import EssayItem from './EssayItem';
import FIBItem from './FibItem';
import ImageItem from './ImageItem';
import InputItem from './InputItem';
import TextItem from './TextItem';
import VideoItem from './VideoItem';
import VocabularyItem from './VocabularyItem';

import './ExerciseItemForm.scss';

const Components = {
    audio: AudioItem,
    boolean: BooleanItem,
    choice: ChoiceItem,
    divider: DividerItem,
    essay: EssayItem,
    fib: FIBItem,
    image: ImageItem,
    input: InputItem,
    text: TextItem,
    video: VideoItem,
    vocabulary: VocabularyItem
};

const labelsByType = {
    audio: 'Аудио',
    boolean: 'Да/Нет',
    choice: 'Выбор',
    divider: 'Разделитель',
    essay: 'Эссе',
    fib: 'Заполнить пробелы',
    image: 'Изображение',
    input: 'Ввод',
    text: 'Текст',
    video: 'Видео',
    vocabulary: 'Список слов'
};

const defaultItem = {
    type: '',
    props: {}
};

export default function ExerciseItemForm({
    item = defaultItem,
    onCancel,
    onSubmit,
    ...props
}) {
    const itemRef = useRef();

    const [isLoading, setLoading] = useState(false);

    const handleSubmit = useCallback(event => {
        event.preventDefault();

        const file = itemRef.current.file;
        const props = itemRef.current.props || {};

        if (file) {
            file.path = props.path || `courses/${item.courseId}/${item.type}s`;
        }

        const data = { file, props };

        setLoading(true);

        if (data instanceof Promise) {
            data.then(onSubmit);
        } else {
            onSubmit(data);
        }
    }, [item, onSubmit]);

    const editorContextValue = useMemo(() => ({
        uploadPath: `courses/${item.courseId}/images`
    }), [item.courseId]);

    const classNames = classnames('ExerciseItemForm', `${capitalize(item.type)}ItemForm`);

    return (
        <form
            id={item.id}
            className={classNames}
            {...props}
        >
            <Flex
                space="between"
                alignItems="center"
                justifyContent="space-between"
                sx={{ padding: '4px 4px 4px 8px' }}
            >
                <Text>{labelsByType[item.type]}</Text>

                <IconButton.Group
                    buttons={[
                        {
                            key: 'save',
                            icon: 'save',
                            title: 'Сохранить',
                            disabled: isLoading,
                            onClick: handleSubmit
                        },
                        {
                            key: 'close',
                            type: 'button',
                            icon: 'close',
                            title: 'Закрыть',
                            onClick: onCancel
                        }
                    ]}
                    color="neutral"
                    size="sm"
                    variant="plain"
                />
            </Flex>

            <ContentEditorContext.Provider value={editorContextValue}>
                {createElement(Components[item.type], {
                    key: item.id,
                    ref: itemRef,
                    ...item.props
                })}
            </ContentEditorContext.Provider>
        </form>
    );
}